import { Box, Heading, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../../../common/formatting/formatNumber";
import {
  Populater,
  useGetProductGroupsForSourcingEventQuery,
  useListBidV2SForEventQuery,
  type ProductDto,
  type ReceivedBidV2Dto,
  type SourcingEventV2Dto,
} from "../../../../autogen/bff-api";

export const ProductAwardSummary = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const { data: groupsData } = useGetProductGroupsForSourcingEventQuery({ sourcingEventId: event.id });
  const { data: bidsData } = useListBidV2SForEventQuery({ eventId: event.id });

  const selection = event.selectedProductBids ?? [];
  const groups = groupsData?.productGroups ?? [];
  if (selection.length === 0 || groups.length === 0) return null;

  const bidById = (bidId: string) => bidsData?.bids.find((bid) => bid.id === bidId);
  const supplierNameOf = (bidId: string) => bidById(bidId)?.ownerName ?? bidId;

  const awardedPriceOf = (groupId: string, product: ProductDto, winnerBidIds: string[]): number | null => {
    const quantity = product.quantity.value;
    const prices = winnerBidIds
      .map((bidId) => unitPriceOf(bidById(bidId), groupId, product.id))
      .filter((unitPrice): unitPrice is number => unitPrice != null)
      .map((unitPrice) => (quantity != null ? unitPrice * Number(quantity) : unitPrice));
    return prices.length ? prices.reduce((sum, price) => sum + price, 0) : null;
  };

  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p={{ base: "4", md: "6" }} mt="6">
      <Heading size="sm" pb="1">
        {t("Product allocation")}
      </Heading>
      <Text fontSize="sm" color="smMuted" pb="3">
        {t("The winning bid selected per product during evaluation.")}
      </Text>
      {groups.map((group) => {
        const groupSelection = selection.find((s) => s.productGroupId === group.id);
        if (!groupSelection?.bids.some((product) => product.bids.length > 0)) return null;
        const nameField = group.productFields.find((field) => field.populatedBy === Populater.Buyer);
        return (
          <Box key={group.id} pb="4">
            <Text fontWeight="semibold" pb="2">
              {group.name}
            </Text>
            <TableContainer border="1px solid" borderColor="smBorder" rounded="md">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>{nameField?.name ?? t("Product")}</Th>
                    <Th>{t("Awarded to")}</Th>
                    <Th textAlign="right">{t("Price")}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {group.products.map((product) => {
                    const winners = groupSelection.bids.find((p) => p.productId === product.id)?.bids ?? [];
                    if (winners.length === 0) return null;
                    const rawName = product.productFields.find((field) => field.name === nameField?.name)?.value;
                    const name = rawName != null && String(rawName).trim() !== "" ? String(rawName) : "—";
                    const price = awardedPriceOf(group.id, product, winners);
                    return (
                      <Tr key={product.id}>
                        <Td>{name}</Td>
                        <Td>{winners.map(supplierNameOf).join(", ")}</Td>
                        <Td textAlign="right">{price != null ? formatNumber(price) : "—"}</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}
    </Box>
  );
};

const unitPriceOf = (bid: ReceivedBidV2Dto | undefined, groupId: string, productId: string): number | null => {
  const value = bid?.products
    ?.find((groupBid) => groupBid.productGroupId === groupId)
    ?.productBids.find((productBid) => productBid.productId === productId)?.unitPrice.value;
  return value != null ? Number(value) : null;
};
